// src/components/BuildingViews/FletchingStationView.js
import React, { useState } from 'react';
import styles from './ForgeView.module.css'; // Reuse forge styles for now
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const FletchingStationView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.fletchingStation?.activeCrafting;

  const fletchingRecipes = [
    {
      id: 'craftBasicBow',
      name: 'Craft Basic Bow',
      description: 'Simple bow for ranged combat',
      input: { 'wood': 5, 'string': 2 },
      output: { 'basicBow': 1 },
      duration: 180, // 3 minutes
      icon: 'wearables/bow.png'
    },
    {
      id: 'craftCopperFishingRod',
      name: 'Craft Copper Fishing Rod',
      description: 'Improved fishing rod with better catch rate',
      input: { 'copperBar': 2, 'plank': 3, 'string': 1 },
      output: { 'copperFishingRod': 1 },
      duration: 240, // 4 minutes
      icon: 'wearables/copperFishingRod.png'
    },
    {
      id: 'craftIronFishingRod',
      name: 'Craft Iron Fishing Rod',
      description: 'High-quality fishing rod for rare fish',
      input: { 'ironBar': 2, 'plank': 4, 'string': 2 },
      output: { 'ironFishingRod': 1 },
      duration: 300, // 5 minutes
      icon: 'wearables/ironFishingRod.png'
    }
  ];

  const canCraftRecipe = (recipe) => {
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    onPlayerAction(recipe.id);
    
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'fletchingStation' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'fletchingStation' });
  };

  const buildingLevel = playerData.buildings?.fletchingStation?.upgradeLevel || 0;
  const efficiencyBonus = buildingLevel * 10;

  return (
    <div className={styles.forgeContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.forgeHeader}>
        <img src="/img/buildingItems/fletchingStation.png" alt="Fletching Station" className={styles.forgeIcon} />
        <div className={styles.forgeInfo}>
          <h2>Fletching & Archery</h2>
          <p>Level {buildingLevel} {efficiencyBonus > 0 && `(-${efficiencyBonus}% time)`}</p>
          <p className={styles.description}>
            Craft bows and fishing rods for ranged combat and fishing
          </p>
        </div>
      </div>

      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🏹 Currently Crafting</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {fletchingRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft || activeCrafting ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.craftTime}>
                  <span>⏱️ {Math.floor(recipe.duration / 60)}m {recipe.duration % 60}s</span>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || activeCrafting || isProcessing}
                className={`${styles.craftButton} ${!canCraft || activeCrafting ? styles.disabled : ''}`}
              >
                {isProcessing ? '⏳ Processing...' : 
                 activeCrafting ? '🔨 Busy' : 
                 !canCraft ? '❌ Missing Items' : 
                 '🏹 Craft'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FletchingStationView; 